import { clsx } from 'clsx'
import { type FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { Button } from 'base-ui'

import { useGlobalConfigContext } from '../../../../global-config'
import { NextLink } from '../../../../next-link'
import { TENANTS_TO_SHOW_RX_NOT_IN_CATALOG_PRODUCTS } from '../../../../tenant'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorIsProductNotInCatalog } from '../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../model/productAccessor/productAccessorIsProductOutOfStock'
import { useProductPageContext } from '../../productPageContext'

import { ProductAddToCartCta } from './ProductAddToCartCta/ProductAddToCartCta'
import { ProductAddToCartQuantitySelection } from './ProductAddToCartQuantitySelection/ProductAddToCartQuantitySelection'
import { ProductOutOfStockSendReminder } from './ProductOutOfStockSendReminder'

const useShouldShowOutOfStockReminder = (): {
  shouldShowOutOfStockReminder: boolean
} => {
  const { currentVariant } = useProductPageContext()
  const { tenant } = useGlobalConfigContext()

  const shouldShowOutOfStockReminder = useMemo((): boolean => {
    const showRxNotInCatalogForSpecificTenant =
      productAccessorIsProductNotInCatalog(currentVariant.stock_status) &&
      productAccessorIsPrescriptionProduct(currentVariant) &&
      TENANTS_TO_SHOW_RX_NOT_IN_CATALOG_PRODUCTS.includes(tenant)

    return (
      productAccessorIsProductOutOfStock(currentVariant.stock_status) ||
      showRxNotInCatalogForSpecificTenant
    )
  }, [currentVariant, tenant])

  return {
    shouldShowOutOfStockReminder,
  }
}

export const ProductAddToCartButtonRow: FC<{
  renderPrescriptionSelect: boolean
}> = ({ renderPrescriptionSelect }) => {
  const { followUpProduct, product } = useProductPageContext()

  const { shouldShowOutOfStockReminder } = useShouldShowOutOfStockReminder()

  if (
    productAccessorIsProductOutOfSale(product.stock_status) &&
    followUpProduct?.canonical_url
  ) {
    return (
      <Button<typeof NextLink>
        className="grow"
        href={followUpProduct.canonical_url}
        slots={{ root: NextLink }}
      >
        <FormattedMessage id="productVariants.link.toSuccessor" />
      </Button>
    )
  }

  return (
    <>
      <ProductAddToCartQuantitySelection
        className={clsx(!renderPrescriptionSelect && 'w-[108px]')}
      />
      {shouldShowOutOfStockReminder ? (
        <ProductOutOfStockSendReminder />
      ) : (
        <ProductAddToCartCta
          className={clsx(renderPrescriptionSelect ? 'col-span-3' : 'grow')}
        />
      )}
    </>
  )
}
